"use client";

import Link from "next/link";
import Image from "next/image";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useState, useRef, useEffect } from "react";
import { Globe, Key } from "lucide-react";
import CreateButton from "./CreateButton";
import { Footer } from "./Footer";
import AuthModal from "../ui/AuthModal";
import { ChangePasswordModal } from "../ui";
import { useAuth } from "@/context/AuthContext/AuthContext";
import { useAuthModal } from "@/context/AuthModalContext";
import { useLanguage } from "@/context/LanguageContext";

const Icons = {
  news: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9.5a2.5 2.5 0 00-2.5-2.5H15" />
    </svg>
  ),
  events: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  forum: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
  ),
  resources: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
    </svg>
  ),
  volunteers: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
    </svg>
  ),
  providers: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  ),
  groups: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  ),
  users: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  ),
  blog: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
    </svg>
  ),
  about: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  ourWork: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
    </svg>
  ),
  welcome: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    </svg>
  ),
  donate: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
    </svg>
  ),
  faq: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  guidelinesLegal: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  profile: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  myForums: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
  ),
  userManagement: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  ),
  activityLog: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
    </svg>
  ),
  flaggedItems: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
    </svg>
  ),
};

function SidebarContent({ onClose, pathname, isAdmin, isAuthenticated }: { onClose?: () => void; pathname: string; isAdmin?: boolean; isAuthenticated?: boolean }) {
  const { t } = useLanguage();
  // The bare "/" route renders the News Feed for logged-in users and the
  // Welcome page for logged-out users. Treat "/" as an alias of whichever
  // page it currently shows so the matching sidebar entry stays highlighted.
  const rootAlias = isAuthenticated ? "/news" : "/welcome";
  const isActive = (href: string) =>
    pathname === href ||
    (href !== "/" && pathname.startsWith(href)) ||
    (pathname === "/" && href === rootAlias);

  const mainNavItems = [
    { label: t.nav.welcome, href: "/welcome", icon: Icons.welcome, testid: "nav-sidebar-welcome-link" },
    { label: t.nav.newsFeed, href: "/news", icon: Icons.news, testid: "nav-sidebar-news-link" },
    { label: t.nav.eventCalendar, href: "/events", icon: Icons.events, testid: "nav-sidebar-events-link" },
    { label: t.nav.groupsDirectory, href: "/groups", icon: Icons.groups, testid: "nav-sidebar-groups-link" },
    { label: t.nav.mentalHealthResources, href: "/resources", icon: Icons.resources, testid: "nav-sidebar-resources-link" },
    { label: t.nav.mentalHealthProviders, href: "/providers", icon: Icons.providers, testid: "nav-sidebar-providers-link" },
    { label: t.nav.residentDirectory, href: "/users", icon: Icons.users, requiresAuth: true, testid: "nav-sidebar-users-link" },
    { label: t.nav.blogArticles, href: "/blog", icon: Icons.blog, testid: "nav-sidebar-blog-link" },
    { label: t.nav.frequentQuestions, href: "/faqs", icon: Icons.faq, testid: "nav-sidebar-faqs-link" },
    { label: t.nav.guidelinesLegal, href: "/guidelines-legal", icon: Icons.guidelinesLegal, testid: "nav-sidebar-guidelines-legal-link" },
  ].filter((item) => !item.requiresAuth || isAuthenticated);

  const togetherWeItems = [
    { label: t.nav.about, href: "/about", icon: Icons.about, testid: "nav-sidebar-about-link" },
    { label: t.nav.ourWork, href: "/our-work", icon: Icons.ourWork, testid: "nav-sidebar-our-work-link" },
    { label: t.nav.donate, href: "/donate", icon: Icons.donate, testid: "nav-sidebar-donate-link" },
  ];

  const secondaryItems = [
    ...(isAuthenticated ? [
      { label: t.nav.myProfile, href: "/my-profile", icon: Icons.profile, testid: "nav-sidebar-my-profile-link" },
      { label: t.nav.myGroups, href: "/my-groups", icon: Icons.myForums, testid: "nav-sidebar-my-groups-link" },
      { label: t.nav.myEvents, href: "/my-events", icon: Icons.events, testid: "nav-sidebar-my-events-link" },
    ] : []),
    ...(isAdmin ? [
      { label: t.userManagement.title, href: "/administrator/user-management", icon: Icons.userManagement, testid: "nav-sidebar-admin-user-mgmt-link" },
      { label: t.flaggedItems?.title || 'Flagged Items', href: "/administrator/flagged-items", icon: Icons.flaggedItems, testid: "nav-sidebar-admin-flagged-link" },
      { label: t.activityLog?.title || 'Activity Log', href: "/administrator/activity-log", icon: Icons.activityLog, testid: "nav-sidebar-admin-activity-link" },
    ] : []),
  ];

  return (
    <nav className="p-4 space-y-1">
      {mainNavItems.map((item) => (
        <Link
          key={item.href}
          href={item.href}
          onClick={onClose}
          data-testid={item.testid}
          className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition ${
            isActive(item.href)
              ? "bg-primary text-background"
              : "text-foreground hover:bg-sidebar-hover"
          }`}
        >
          {item.icon}
          {item.label}
        </Link>
      ))}
      <div className="pt-4 mt-4 border-t border-border">
        {togetherWeItems.map((item) => (
          <Link
            key={item.href}
            href={item.href}
            onClick={onClose}
            data-testid={item.testid}
            className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition ${
              isActive(item.href)
                ? "bg-primary text-background"
                : "text-foreground hover:bg-sidebar-hover"
            }`}
          >
            {item.icon}
            {item.label}
          </Link>
        ))}
      </div>
      {secondaryItems.length > 0 && <div className="pt-4 mt-4 border-t border-border">
        {secondaryItems.map((item) => (
          <Link
            key={item.href}
            href={item.href}
            onClick={onClose}
            data-testid={item.testid}
            className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition ${
              isActive(item.href)
                ? "bg-primary text-background"
                : "text-foreground hover:bg-sidebar-hover"
            }`}
          >
            {item.icon}
            {item.label}
          </Link>
        ))}
      </div>}
    </nav>
  );
}

export default function NavBar({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const pathname = usePathname();
  const router = useRouter();
  const { currentUser, loading, signOut } = useAuth();
  const { isAuthModalOpen, authModalMode, openAuthModal, closeAuthModal } = useAuthModal();
  const { language, toggleLanguage, t } = useLanguage();
  const userMenuRef = useRef<HTMLDivElement>(null);
  const searchParams = useSearchParams();

  // Close user menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Auto-open login modal when redirected from email verification.
  // Wait for auth hydration to complete before checking currentUser so we
  // don't open the modal for a user who is already signed in.
  useEffect(() => {
    if (searchParams.get("openLogin") === "true" && !loading && !currentUser) {
      openAuthModal("login");
      const url = new URL(window.location.href);
      url.searchParams.delete("openLogin");
      window.history.replaceState(null, "", url.toString());
    }
  }, [searchParams, loading, currentUser, openAuthModal]);

  // Close the auth modal if the user signs in while it is open.
  useEffect(() => {
    if (currentUser && isAuthModalOpen) {
      closeAuthModal();
    }
  }, [currentUser, isAuthModalOpen, closeAuthModal]);

  const handleSignOut = async () => {
    await signOut();
    setIsUserMenuOpen(false);
    router.push("/");
  };

  return (
    <>
      {/* Top Bar */}
      <header className="bg-primary text-background sticky top-0 z-50">
        <div className="max-w-[1500px] mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center gap-3">
            {/* Mobile menu button */}
            <button
              onClick={() => setIsOpen(true)}
              className="md:hidden p-1 hover:bg-primary-light rounded transition"
              data-testid="nav-mobile-menu-toggle"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            {/* Logo */}
            <Link href="/" className="hidden min-[400px]:flex items-center p-1" data-testid="nav-logo-link">
              <Image
                src="/TWPlatformHubLogo.png"
                alt="TogetherWe Community Hub"
                width={522}
                height={122}
                priority
                className="h-8 w-auto sm:h-12"
              />
            </Link>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-1.5 px-2 py-1.5 text-sm font-medium hover:bg-white/10 rounded-md transition"
              aria-label={language === 'en' ? 'Switch to Spanish' : 'Cambiar a Inglés'}
              data-testid="nav-lang-toggle"
            >
              <Globe size={18} />
              <span className="hidden sm:inline">{language === 'en' ? t.language.english : t.language.spanish}</span>
            </button>
            <CreateButton />
            {currentUser ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-white/10 transition"
                  data-testid="nav-user-menu-btn"
                >
                  {currentUser.avatarUrl ? (
                    <Image
                      src={currentUser.avatarUrl}
                      alt={currentUser.displayName}
                      width={32}
                      height={32}
                      className="w-8 h-8 rounded-full object-contain"
                    />
                  ) : (
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
                      style={{ backgroundColor: currentUser.avatarColor || "#00423E" }}
                    >
                      {currentUser.initials}
                    </div>
                  )}
                  <span className="hidden sm:inline text-sm font-medium">{currentUser.displayName}</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-background border border-border rounded-md shadow-lg py-1 z-50" data-testid="nav-user-menu-dropdown">
                    <Link
                      href="/my-profile"
                      onClick={() => setIsUserMenuOpen(false)}
                      className="block px-4 py-2 text-sm text-foreground hover:bg-sidebar-hover"
                      data-testid="nav-profile-link"
                    >
                      {t.nav.myProfile}
                    </Link>
                    <button
                      onClick={() => {
                        setIsUserMenuOpen(false);
                        setIsChangePasswordModalOpen(true);
                      }}
                      className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-foreground hover:bg-sidebar-hover"
                      data-testid="nav-change-pw-btn"
                    >
                      <Key size={16} />
                      {t.auth.changePassword}
                    </button>
                    <button
                      onClick={handleSignOut}
                      className="block w-full text-left px-4 py-2 text-sm text-foreground hover:bg-sidebar-hover"
                      data-testid="nav-sign-out-btn"
                    >
                      {t.nav.logOut}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={() => openAuthModal('signup')}
                className="px-3 sm:px-4 py-1.5 text-sm font-medium bg-white text-foreground rounded-md hover:bg-gray-100 transition"
                data-testid="nav-sign-up-btn"
              >
                <span className="sm:hidden">{t.nav.signIn}</span>
                <span className="hidden sm:inline">{t.nav.signUp}</span>
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Auth Modal */}
      <AuthModal isOpen={isAuthModalOpen} onClose={closeAuthModal} initialMode={authModalMode} />

      {/* Main layout container with max-width */}
      <div className="max-w-[1500px] mx-auto flex">
        {/* Desktop Sidebar */}
        <aside className="hidden md:block w-75 shrink-0  border-r border-l border-border sticky top-[57px] h-[calc(100vh-57px)] overflow-y-auto bg-white">
          <SidebarContent pathname={pathname || "/"} isAdmin={currentUser?.role?.type === 'admin' || currentUser?.role?.type === 'superadmin'} isAuthenticated={!!currentUser} />
        </aside>

        {/* Main content + footer (MWBI-901). The column wrapper keeps the footer
            inside the content column, and `flex-1` on <main> plus the min-height
            pin the band to the bottom of the viewport on a short page. Measured:
            without the min-height, /my-groups at 375px left 353px of empty page
            below the footer. It only does real work below md, where the sidebar
            is display:none and so cannot supply the floor itself -- hence dvh
            rather than the sidebar's vh, since mobile browser chrome makes vh
            overshoot.

            The 57px is the same approximation the sidebar above already commits
            to; the real header measures 56px at 375px and grows to 80px by
            768px. Deliberately erring on the small side: subtracting too little
            costs at most ~23px of extra scroll on a short page in the 640-767px
            band, whereas subtracting too much reopens the dead-space gap this
            exists to close.

            <footer> must stay OUT of <main>: nested in a main/section/article it
            stops mapping to the contentinfo landmark. */}
        <div className="flex-1 min-w-0 flex flex-col min-h-[calc(100dvh-57px)]" data-testid="content-column">
          <main className="flex-1 min-w-0">{children}</main>
          <Footer />
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {isOpen && (
        <div className="md:hidden fixed inset-0 z-50" data-testid="nav-mobile-sidebar">
          <div className="absolute inset-0 bg-black/50" onClick={() => setIsOpen(false)} />
          <aside className="absolute left-0 top-0 w-64 h-full bg-background shadow-lg overflow-y-auto">
            <div className="p-4 border-b border-border flex justify-between items-center">
              <span className="font-semibold">{t.nav.menu}</span>
              <button onClick={() => setIsOpen(false)} className="p-1 hover:bg-sidebar-hover rounded" data-testid="nav-mobile-sidebar-close-btn">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <SidebarContent pathname={pathname || "/"} onClose={() => setIsOpen(false)} isAdmin={currentUser?.role?.type === 'admin' || currentUser?.role?.type === 'superadmin'} isAuthenticated={!!currentUser} />
          </aside>
        </div>
      )}

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={isChangePasswordModalOpen}
        onClose={() => setIsChangePasswordModalOpen(false)}
      />
    </>
  );
}


